import { UserSession } from "@/types/session";
import { createContext } from "react";
import { ChatEntry } from "@/types/chat";
import { ClosedSessionsData, SessionsData } from "./SessionProvider";

export interface SessionContextType {
  sessions: SessionsData;
  closedSessions: ClosedSessionsData;
  isLoading: boolean;
  /** True only while fetching closed-session history (does not block active chat). */
  isLoadingClosedSessions: boolean;
  getSession: (chatMode: ChatEntry, closeIfExpired?: boolean) => Promise<UserSession | null | undefined>;
  loadClosedSessions: (limit?: number) => Promise<UserSession[]>;
  createNewSession: (chatMode: ChatEntry) => Promise<UserSession>;
  extendSession: (sessionId: UserSession["id"]) => Promise<void>;
  closeSession: (session: UserSession) => Promise<void>;
  getClosedUnratedSessions: () => Promise<UserSession[]>;
  rateSessions: (sessionIds: UserSession["id"][], rating: 0 | 1) => Promise<void>;
  getSessionById: (sessionId: UserSession["id"]) => Promise<UserSession | null>;
  /** Reopens a saved (closed) session and makes it the active thread for its chat mode. */
  reopenSessionFromHistory: (sessionId: UserSession["id"]) => Promise<UserSession | null>;
  /** Update only the user-facing `session_name`; clears it when value is empty. */
  renameSession: (
    sessionId: UserSession["id"],
    sessionName: string | null,
  ) => Promise<UserSession | null>;
  /** Delete the session and its chat history. Also removes from `closedSessions` cache. */
  deleteSession: (sessionId: UserSession["id"]) => Promise<boolean>;
}

export const SessionContext = createContext<SessionContextType>({
  sessions: null,
  closedSessions: null,
  isLoading: false,
  isLoadingClosedSessions: false,
  getSession: () => Promise.resolve(),
  loadClosedSessions: () => Promise.resolve([]),
  createNewSession: () => Promise.resolve(null),
  extendSession: () => Promise.resolve(),
  closeSession: () => Promise.resolve(),
  getClosedUnratedSessions: () => Promise.resolve([]),
  rateSessions: () => Promise.resolve(),
  getSessionById: () => Promise.resolve(null),
  reopenSessionFromHistory: () => Promise.resolve(null),
  renameSession: () => Promise.resolve(null),
  deleteSession: () => Promise.resolve(false),
});
